import http from 'services/http';

export type SftpAccess = 'inbound' | 'outbound' | 'both';
export type SftpSyncState = 'pending' | 'synced' | 'failed';
export type SftpDeliverable = 'providerDocuments' | 'patientExport';

export type SftpUser = {
  id: string;
  clientId: string | null;
  username: string;
  access: SftpAccess;
  inbound: boolean;
  outbound: boolean;
  keyFingerprint: string;
  keyCount: number;
  alsoUsedBy: string[];
  active: boolean;
  syncState: SftpSyncState;
  lastSyncError: string | null;
  syncedAt: string | null;
  lastChangedBy: { id: string; name: string } | null;
};

export type SftpExportConfiguration = {
  pathPrefix: string;
  enabled: boolean;
  enabledDeliverableTypes: Partial<Record<SftpDeliverable, boolean>>;
};

export type SftpUsersIndex = {
  data: SftpUser[];
  exportConfiguration: SftpExportConfiguration | null;
};

export type SftpKeyPreview = {
  type: string;
  bits: number;
  fingerprint: string;
  alsoUsedBy: { username: string; clientName: string | null }[];
};

export type SftpUserParams = {
  username?: string;
  sshPublicKey?: string;
  inbound?: boolean;
  outbound?: boolean;
  active?: boolean;
  deliverables?: Record<SftpDeliverable, boolean>;
};

export const sftpUserQueryKeys = {
  index: (clientId: string) => ['adminSftpUsers', clientId] as const,
};

const url = (clientId: string) => `admin/clients/${clientId}/sftp_users`;

export function indexSftpUsers(clientId: string, signal?: AbortSignal): Promise<SftpUsersIndex> {
  return http.get<SftpUsersIndex>(url(clientId), { signal }).then((res) => res.data);
}

export function createSftpUser(clientId: string, params: SftpUserParams): Promise<SftpUser> {
  return http.post<SftpUser>(url(clientId), params).then((res) => res.data);
}

export function updateSftpUser(clientId: string, id: string, params: SftpUserParams): Promise<SftpUser> {
  return http.patch<SftpUser>(`${url(clientId)}/${id}`, params).then((res) => res.data);
}

export function previewSftpKey(clientId: string, sshPublicKey: string): Promise<SftpKeyPreview> {
  return http.post<SftpKeyPreview>(`${url(clientId)}/key_preview`, { sshPublicKey }).then((res) => res.data);
}
